import {
  Modal,
  Stack,
  TextInput,
  Button,
  Group,
  Text,
  Code,
  CopyButton,
  ActionIcon,
  Tooltip,
} from '@mantine/core'
import { useForm } from '@mantine/form'
import { showNotification } from '@mantine/notifications'
import { useState } from 'react'
import { IconCopy, IconCheck } from '@tabler/icons-react'
import { useClient } from '@/hooks/useClient'
import { normalizeErrorString } from '@medplum/core'

type Props = {
  opened: boolean
  onClose: () => void
  onCreated: () => void
}

type FormValues = {
  recipientEmail: string
}

export function CreateOrganizationConnectModal({ opened, onClose, onCreated }: Props) {
  const client = useClient()
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [acceptUrl, setAcceptUrl] = useState<string | null>(null)

  const form = useForm<FormValues>({
    initialValues: { recipientEmail: '' },
    validate: {
      recipientEmail: (value) =>
        /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim()) ? null : 'Enter a valid email',
    },
  })

  const handleClose = () => {
    setAcceptUrl(null)
    form.reset()
    onClose()
  }

  const handleSubmit = async (values: FormValues) => {
    setIsSubmitting(true)
    try {
      const result = await client.createOrganizationConnect({
        recipientEmail: values.recipientEmail.trim(),
      })
      setAcceptUrl(result.acceptUrl)
      onCreated()
    } catch (error) {
      showNotification({
        color: 'red',
        title: 'Failed to create connection',
        message:
          normalizeErrorString(error) || 'Unable to create the connection. Please try again.',
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Modal opened={opened} onClose={handleClose} title="Invite organization to connect">
      {acceptUrl ? (
        <Stack>
          <Text size="sm">
            Invitation sent. You can also share the link below directly with the recipient:
          </Text>
          <Group wrap="nowrap" align="center">
            <Code style={{ flex: 1, overflow: 'auto', wordBreak: 'break-all' }}>{acceptUrl}</Code>
            <CopyButton value={acceptUrl} timeout={1500}>
              {({ copied, copy }) => (
                <Tooltip label={copied ? 'Copied' : 'Copy'}>
                  <ActionIcon variant="light" onClick={copy}>
                    {copied ? <IconCheck size={16} /> : <IconCopy size={16} />}
                  </ActionIcon>
                </Tooltip>
              )}
            </CopyButton>
          </Group>
          <Group justify="flex-end">
            <Button onClick={handleClose}>Done</Button>
          </Group>
        </Stack>
      ) : (
        <form onSubmit={form.onSubmit(handleSubmit)}>
          <Stack>
            <TextInput
              label="Recipient email"
              placeholder="admin@partner-org.com"
              required
              {...form.getInputProps('recipientEmail')}
            />
            <Text size="xs" c="dimmed">
              We'll send an invitation email with a link the recipient can click to accept the
              connection.
            </Text>
            <Group justify="flex-end">
              <Button variant="default" onClick={handleClose} disabled={isSubmitting}>
                Cancel
              </Button>
              <Button type="submit" loading={isSubmitting}>
                Send invitation
              </Button>
            </Group>
          </Stack>
        </form>
      )}
    </Modal>
  )
}
